import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/apiClient";
import { track } from "../lib/posthog";

const CartContext = createContext(null);

const uid = () =>
  (crypto?.randomUUID ? crypto.randomUUID() : "sess-" + Math.random().toString(36).slice(2) + Date.now());

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("neostore_cart") || "[]");
      return raw.map((i) => ({ ...i, key: i.key || `${i.product_id}::${i.variation_id || ""}` }));
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [freeGift, setFreeGift] = useState(null);
  const [bogo, setBogo] = useState(false);
  const [saleOn, setSaleOn] = useState(false);
  const [sessionId] = useState(() => {
    let s = localStorage.getItem("neostore_session");
    if (!s) {
      s = uid();
      localStorage.setItem("neostore_session", s);
    }
    return s;
  });

  useEffect(() => {
    localStorage.setItem("neostore_cart", JSON.stringify(items));
  }, [items]);

  // Free-gift promotion config (null when the promo is off).
  useEffect(() => {
    api.get("/promo/free-gift")
      .then((r) => setFreeGift(r.data?.enabled ? r.data : null))
      .catch((e) => console.debug("free-gift config load failed", e));
  }, []);

  // Buy X Get X Free promotion — on/off.
  useEffect(() => {
    api.get("/promo/bogo")
      .then((r) => setBogo(!!r.data?.enabled))
      .catch((e) => console.debug("bogo config load failed", e));
  }, []);

  // Sitewide "40% OFF" display sale — on/off.
  useEffect(() => {
    api.get("/promo/sitewide")
      .then((r) => setSaleOn(!!r.data?.enabled))
      .catch((e) => console.debug("sitewide sale config load failed", e));
  }, []);

  // Live cart ping: keep the admin "Live Carts" view fresh as shoppers browse.
  useEffect(() => {
    if (!items.length) return;
    const t = setTimeout(() => {
      api.post("/carts/track", {
        session_id: sessionId,
        items,
        source: "live",
        payment_intent_status: "Active cart",
      }).catch((e) => console.debug("live-cart ping failed (non-blocking)", e));
    }, 1500);
    return () => clearTimeout(t);
  }, [items, sessionId]);

  const addItem = useCallback((product, qty = 1, variant = null) => {
    const variation_id = variant?.variation_id || null;
    const label = variant?.label && variant.label !== "Default" ? variant.label : null;
    const price = variant?.price != null ? variant.price : product.price;
    const key = `${product.id}::${variation_id || ""}`;
    track("add_to_cart", {
      product_id: product.id, name: product.name, variant: label,
      price, quantity: qty, value: Number((price * qty).toFixed(2)),
    });
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + qty } : i));
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          variation_id,
          variant: label,
          name: product.name,
          price,
          quantity: qty,
          image_url: variant?.image_url || product.image_url,
        },
      ];
    });
    setDrawerOpen(true);
  }, []);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, quantity: Math.max(0, qty) } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  const trackAbandoned = useCallback(
    async (extra = {}) => {
      if (!items.length) return;
      try {
        await api.post("/carts/track", {
          session_id: sessionId,
          items,
          source: extra.source || "checkout",
          payment_intent_status: extra.payment_intent_status || "Draft",
          ...extra,
        });
      } catch (e) {
        console.debug("abandoned-cart tracking failed (non-blocking)", e);
      }
    },
    [items, sessionId]
  );

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateQty,
        removeItem,
        clearCart,
        subtotal,
        count,
        drawerOpen,
        setDrawerOpen,
        sessionId,
        trackAbandoned,
        freeGift: bogo ? null : freeGift,
        bogo,
        saleOn,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
